import React, {useState, useContext, useEffect, useMemo} from 'react';
import '../Components/modal.css';
import './edit.css';
import './DownloadReportShared.css';

import axios from 'axios';
import { BASE_URL_API } from '../Constants';
import Select from 'react-select';

import { useAuth } from '../hooks/AuthProvider';
import Alert from '../Components/Alert';
import AiReportModePanel from '../Components/AiReportModePanel';
import AiReportVariablesStep from '../Components/AiReportVariablesStep';

import { LanguageContext } from '../context/LanguageContext';
import dashboardTranslations from '../languages/downloadReport.json';

import { getErrorMessage } from '../utils/errorUtils';
import { buildDeviceDropdownOption } from '../utils/dropdownLabels';
import { isReportDownloadDatastream } from '../utils/datastreamValidation';
import { getAiReportTypeConfig, sanitizeAiReportVariablesForType, useAiReportTypes } from '../utils/aiReportTypes';
import {
  buildReportAccess,
  downloadFileFromUrl,
  getReportModeUi,
  isNoDataReportResponse,
  REPORT_DOWNLOAD_MODES,
  resolveAiReportMode,
  resolveReportMode,
} from '../utils/reportDownload';
import { CustomSwitch } from '../svg/UtilsIcons';
import { getOrganizationRowId, normalizeOrganizationListRow } from '../utils/listRowIds';

import dayjs from 'dayjs';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

const buildOrganizationNameMap = (organizations) =>
  (Array.isArray(organizations) ? organizations : []).reduce((acc, organization) => {
    const id = Number(getOrganizationRowId(organization));
    const name = String(organization?.name || '').trim();
    if (Number.isFinite(id) && name) {
      acc[id] = name;
    }
    return acc;
  }, {});

const resolveDeviceOrganizationName = (device, organizationNamesById = {}) => {
  const directName = String(device?.organization_name || '').trim();
  if (directName) return directName;

  const nestedName = String(device?.organization?.name || '').trim();
  if (nestedName) return nestedName;

  const orgId = Number(device?.organization_id ?? device?.organization?.id);
  if (Number.isFinite(orgId)) {
    return String(organizationNamesById?.[orgId] || '').trim();
  }

  return '';
};

const DEFAULT_NO_DATA_MESSAGE =
  'There is no data available for the selected filters and time range.';

export const DownloadDashboardReport = ({
  state,
  setState,
  devices,
  organizationId,
  dashboardId,
  dashboardType,
  setLoadingReport,
  onAlert,
}) => {
  const { permissions, email: authEmail, user } = useAuth();
  const { lang } = useContext(LanguageContext);

  const canDownloadStandardReport = permissions['download_dashboard_reports'];
  const canDownloadAiReport = permissions['download_ai_reports'];
  const t = dashboardTranslations[lang].downloadReport;
  const isRtl = ['ar', 'ur'].includes(lang);

  const isDarkTheme =
    document.documentElement.classList.contains('dark-theme') ||
    document.body.classList.contains('dark-theme');

  const pickerTheme = useMemo(
    () => createTheme({ palette: { mode: isDarkTheme ? 'dark' : 'light' } }),
    [isDarkTheme]
  );
  const { types: aiReportTypes, availability: aiReportAvailability } = useAiReportTypes();
  const aiReportsEnabledForDashboards = aiReportAvailability.dashboards !== false;
  const reportAccess = useMemo(
    () =>
      buildReportAccess({
        canDownloadStandardReport,
        canDownloadAiReport: canDownloadAiReport && aiReportsEnabledForDashboards,
      }),
    [aiReportsEnabledForDashboards, canDownloadAiReport, canDownloadStandardReport]
  );

  const [number, setNumber] = useState('');
  const [tstart, setTstart] = useState('');
  const [tstop, setTstop] = useState('');
  const [sendToEmail, setSendToEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [addBlockError, setAddBlockError] = useState('');
  const [includeAllDevicesDatastreams, setIncludeAllDevicesDatastreams] = useState(false);
  const [blocks, setBlocks] = useState([{ id: Date.now(), device: null }]);
  const [selectedStreamsByBlockId, setSelectedStreamsByBlockId] = useState({});
  const [organizationNamesById, setOrganizationNamesById] = useState({});
  const [reportMode, setReportMode] = useState(reportAccess.defaultMode);
  const [aiReportVariables, setAiReportVariables] = useState({});

const defaultEmail = (authEmail || user?.email || '').trim();

useEffect(() => {
  if (sendToEmail) {
    setEmail((prev) => {
      if (prev.trim()) return prev;
      return defaultEmail;
    });
  }
}, [sendToEmail, defaultEmail]);

  useEffect(() => {
    if (state?.open) {
      setReportMode(reportAccess.defaultMode);
      setAiReportVariables({});
    }
  }, [state?.open, reportAccess.defaultMode]);

  const [alertState, setAlertState] = useState({
    open: false,
    message: '',
    type: 'info',
    position: isRtl ? 'left' : 'right',
  });

  const aggregationOptions = t.aggregationOptions;
  const resolvedDevices = useMemo(
    () =>
      (Array.isArray(devices) ? devices : []).map((device) => {
        const resolvedOrganizationName = resolveDeviceOrganizationName(device, organizationNamesById);
        if (!resolvedOrganizationName) return device;
        if (String(device?.organization_name || '').trim() || String(device?.organization?.name || '').trim()) {
          return device;
        }
        return { ...device, organization_name: resolvedOrganizationName };
      }),
    [devices, organizationNamesById]
  );
  const deviceSelectOptions = useMemo(
    () => resolvedDevices.map((device) => buildDeviceDropdownOption(device, 'did')),
    [resolvedDevices]
  );

  const showAlert = (type, message) => {
    if (typeof onAlert === 'function') {
      onAlert(type, message);
      return;
    }

    setAlertState({ open: true, type, message, position: isRtl ? 'left' : 'right' });
  };

  const closeReportDialog = () => setState({ open: false, id: 0 });

  const closeDialogWithAlert = (type, message) => {
    if (typeof onAlert === 'function') {
      closeReportDialog();
      onAlert(type, message);
      return;
    }

    showAlert(type, message);
    setTimeout(() => closeReportDialog(), 2000);
  };

  useEffect(() => {
    if (!state?.open) return undefined;

    const devicesMissingOrgNames = resolvedDevices.some((device) => {
      const orgId = Number(device?.organization_id ?? device?.organization?.id);
      return Number.isFinite(orgId) && !resolveDeviceOrganizationName(device, organizationNamesById);
    });

    if (!devicesMissingOrgNames) return undefined;

    let active = true;
    axios
      .get(`${BASE_URL_API}/organization/list`)
      .then((response) => {
        const organizations = Array.isArray(response?.data?.data)
          ? response.data.data.map(normalizeOrganizationListRow)
          : null;
        if (!active || !Array.isArray(organizations)) return;
        setOrganizationNamesById((prev) => ({ ...buildOrganizationNameMap(organizations), ...prev }));
      })
      .catch((error) => {
        console.warn('[DownloadDashboardReport] Failed to preload organization names:', error);
      });

    return () => {
      active = false;
    };
  }, [state?.open, resolvedDevices, organizationNamesById]);

  const parseLocalDateTimeToUnix = (dateTimeStr) => {
    const [datePart, timePart] = dateTimeStr.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);
    return Math.floor(Date.UTC(year, month - 1, day, hour, minute, 0) / 1000);
  };

  const handleAddBlock = () => {
    if (blocks.length > 0) {
      const lastBlock = blocks[blocks.length - 1];
      const hasDevice = !!lastBlock.device;
      const hasDatastreams = (selectedStreamsByBlockId[lastBlock.id]?.length ?? 0) > 0;

      if (!hasDevice || !hasDatastreams) {
        setAddBlockError(t.errorFillInputs);
        return;
      }
    }

    setAddBlockError('');
    const newBlock = { id: Date.now(), device: null };
    setBlocks((prev) => [...prev, newBlock]);
  };

  const handleRemoveBlock = (id) => {
    setBlocks((prev) => prev.filter((b) => b.id !== id));
    setSelectedStreamsByBlockId((prev) => {
      const newObj = { ...prev };
      delete newObj[id];
      return newObj;
    });
  };

  const handleSelectDevice = (blockId, deviceId) => {
    const device = resolvedDevices.find((d) => d.did === deviceId);

    setBlocks((prev) => prev.map((b) => (b.id === blockId ? { ...b, device } : b)));

    setSelectedStreamsByBlockId((prev) => ({
      ...prev,
      [blockId]: [],
    }));

    setAddBlockError('');
  };

  const handleStreamChange = (blockId, filteredStreams, selectedOptions) => {
    const safeOptions = selectedOptions || [];
    const isAllSelected = safeOptions.some((opt) => opt.value === '__ALL__');

    const selectedPins = isAllSelected
      ? filteredStreams.map((ds) => ds.pin)
      : safeOptions.map((opt) => opt.value);

    setSelectedStreamsByBlockId((prev) => ({ ...prev, [blockId]: selectedPins }));
    setAddBlockError('');
  };

  const handleAiReportVariableChange = (fieldName, value) => {
    setAiReportVariables((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleDownload = async () => {
    if (!number || isNaN(number) || number <= 0) return showAlert('error', t.alertInvalidStep);
    if (!tstart || !tstop) return showAlert('error', t.alertSelectTimes);

    const tstartUnix = parseLocalDateTimeToUnix(tstart);
    const tstopUnix = parseLocalDateTimeToUnix(tstop);

    if (tstartUnix >= tstopUnix) return showAlert('error', t.alertTimeOrder);

    if (sendToEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!email || !emailRegex.test(email)) {
        return showAlert('error', t.alertInvalidEmail);
      }
    }

    const ensureOrganizationMap = async () => {
      const selectedDevices = blocks.map((block) => block.device).filter(Boolean);
      const missingOrgNames = selectedDevices.some((device) => {
        const orgId = Number(device?.organization_id ?? device?.organization?.id);
        return Number.isFinite(orgId) && !resolveDeviceOrganizationName(device, organizationNamesById);
      });

      if (!missingOrgNames) {
        return organizationNamesById;
      }

      try {
        const response = await axios.get(`${BASE_URL_API}/organization/list`);
        const organizations = Array.isArray(response?.data?.data)
          ? response.data.data.map(normalizeOrganizationListRow)
          : null;
        if (!Array.isArray(organizations)) {
          return organizationNamesById;
        }
        const nextMap = buildOrganizationNameMap(organizations);
        setOrganizationNamesById((prev) => ({ ...nextMap, ...prev }));
        return nextMap;
      } catch (error) {
        console.warn('[DownloadDashboardReport] Failed to resolve organization names for export:', error);
        return organizationNamesById;
      }
    };

    const organizationNameMap = await ensureOrganizationMap();

    const payloadDevices = blocks
      .map((block) => {
        const device = block.device;
        if (!device || !device?.template?.tid) return null;

        const filteredStreams =
          device.template.datastreams?.filter(isReportDownloadDatastream) || [];

        const selectedPins = selectedStreamsByBlockId[block.id] || [];
        const orgName = resolveDeviceOrganizationName(device, organizationNameMap) || 'No Org';
        const deviceName = device.name || device.did;

        return {
          device: device.did,
          name: deviceName,
          measurement: device.template.tid,
          fields: filteredStreams
            .filter((ds) => selectedPins.includes(ds.pin))
            .map((ds) => ({
              field: ds.pin,
              label: `${orgName} (${deviceName}) (${ds.name})`,
              min: ds.min,
              max: ds.max,
            })),
        };
      })
      .filter((d) => d && d.fields.length > 0);

    if (!includeAllDevicesDatastreams && payloadDevices.length === 0) {
      return showAlert('error', t.errorDeviceFillInputs);
    }

    if (includeAllDevicesDatastreams && !organizationId) {
      return showAlert('error', t.errorMissingOrganization || 'Missing organization context for full export.');
    }

    const resolvedReportMode = resolveReportMode(reportAccess, reportMode);
    const shouldUseAiReport = resolveAiReportMode(reportAccess, reportMode);
    const reportType = dashboardType || null;
    const aiTypeConfig = shouldUseAiReport ? getAiReportTypeConfig(aiReportTypes, reportType) : null;

    if (shouldUseAiReport && !aiTypeConfig) {
      return showAlert('error', 'Please choose an AI report type for this dashboard before generating an AI report.');
    }

    const normalizedAiVariables = sanitizeAiReportVariablesForType(aiReportTypes, reportType, aiReportVariables);
    if (shouldUseAiReport) {
      const missingVars = (aiTypeConfig.params || [])
        .filter((param) => param.required !== false && !normalizedAiVariables[param.key])
        .map((param) => param.key);
      if (missingVars.length > 0) {
        return showAlert('error', `Please fill AI report variables: ${missingVars.join(', ')}`);
      }
    }

    const body = {
      bucket: 'Sensors',
      dashboard_id: dashboardId,
      tstart: tstartUnix,
      tstop: tstopUnix,
      step: number,
      sendToEmail,
      email,
      reportMode: resolvedReportMode,
      includeAiReport: shouldUseAiReport,
      aiReportType: reportType,
      aiReportVariables: normalizedAiVariables,
      includeAllDevicesDatastreams,
      ...(includeAllDevicesDatastreams
        ? { organization_id: organizationId }
        : { devices: payloadDevices }),
    };

    const reportModeUi = getReportModeUi(resolvedReportMode, t.reportModeUi);

    setLoadingReport(true);

    try {
      const response = await axios.post(`${BASE_URL_API}/data/download_dashboard_report`, body);
      const responseData = response.data || {};

      if (isNoDataReportResponse(responseData)) {
        showAlert('info', responseData.message || t.noDataMessage || DEFAULT_NO_DATA_MESSAGE);
        return;
      }

      const { status, downloadUrl, reportMode: backendReportMode, downloadType } = responseData;
      const effectiveBackendMode =
        backendReportMode ||
        (downloadType === 'pdf' || /\.pdf($|\?)/i.test(String(downloadUrl || ''))
          ? REPORT_DOWNLOAD_MODES.AI
          : REPORT_DOWNLOAD_MODES.STANDARD);

      if (effectiveBackendMode !== resolvedReportMode) {
        throw new Error(
          `Expected "${resolvedReportMode}" report mode, but the backend returned "${effectiveBackendMode}".`
        );
      }

      if (sendToEmail) {
        closeDialogWithAlert(
          'success',
          `${reportModeUi.sendToEmailLabel} It will be sent to ${email} shortly.`
        );
      } else if (status === 'ready' && downloadUrl) {
        await downloadFileFromUrl(downloadUrl);
        closeDialogWithAlert('success', reportModeUi.successLabel);
      } else {
        closeDialogWithAlert('warning', t.reportPreparing);
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      showAlert('error', errorMessage);
    } finally {
      setLoadingReport(false);
    }
  };

  if (!state?.open) return null;

  const maxDateTime = dayjs();
  const startValue = tstart ? dayjs(tstart) : null;
  const stopValue = tstop ? dayjs(tstop) : null;

  if (!reportAccess.canOpenReportDialog) {
    return (
      <div className="dr-modal" id="modal" dir={isRtl ? 'rtl' : 'ltr'}>
        <div className="dr-popup dr-popup--autoHeight" id="popup">
          <div className="dr-header modal-header">
            <h2 className="dr-title">{t.permissionDeniedTitle}</h2>
          </div>

          <div className="dr-divider" />

          <div className="dr-content modal-content">
            <p className="dr-subtitle">{t.permissionDeniedDashboardMessage}</p>
          </div>

          <div className="dr-footer modal-footer">
            <button className="btn btn-cancel" onClick={() => setState({ open: false, id: 0 })}>
              {t.buttonClose}
            </button>
            <div />
          </div>
        </div>
      </div>
    );
  }

  const resolvedReportMode = resolveReportMode(reportAccess, reportMode);
  const primaryActionLabel = getReportModeUi(resolvedReportMode, t.reportModeUi).actionLabel;

  return (
    <React.Fragment>
      {typeof onAlert !== 'function' && (
        <Alert
          open={alertState.open}
          close={() => setAlertState({ ...alertState, open: false })}
          message={alertState.message}
          type={alertState.type}
          position={alertState.position}
        />
      )}

      <ThemeProvider theme={pickerTheme}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <div className="dr-modal" id="modal" dir={isRtl ? 'rtl' : 'ltr'}>
            <div className="dr-popup" id="popup">
              <div className="dr-header modal-header">
                <h2 className="dr-title">{t.title}</h2>
              </div>

              <div className="dr-divider" />

              <div className="dr-content modal-content">
                <div className="dr-field form-item">
                  <label className="dr-label form-lbl">{t.labelStart}</label>
                  <div className="dr-muiField">
                    <DateTimePicker
                      value={startValue}
                      format="DD/MM/YYYY hh:mm A"
                      onChange={(newVal) => {
                        setTstart(newVal ? dayjs(newVal).format('YYYY-MM-DDTHH:mm') : '');
                      }}
                      maxDateTime={maxDateTime}
                      ampm
                      slots={{ openPickerIcon: CalendarMonthIcon }}
                      slotProps={{
                        textField: { size: 'small', placeholder: t.placeholderDateTime },
                        popper: { className: 'dr-pickerPopper' },
                        actionBar: { actions: ['clear', 'cancel', 'accept'] },
                      }}
                    />
                  </div>
                </div>

                <div className="dr-field form-item">
                  <label className="dr-label form-lbl">{t.labelEnd}</label>
                  <div className="dr-muiField">
                    <DateTimePicker
                      value={stopValue}
                      format="DD/MM/YYYY hh:mm A"
                      onChange={(newVal) => {
                        setTstop(newVal ? dayjs(newVal).format('YYYY-MM-DDTHH:mm') : '');
                      }}
                      maxDateTime={maxDateTime}
                      ampm
                      slots={{ openPickerIcon: CalendarMonthIcon }}
                      slotProps={{
                        textField: { size: 'small', placeholder: t.placeholderDateTime },
                        popper: { className: 'dr-pickerPopper' },
                        actionBar: { actions: ['clear', 'cancel', 'accept'] },
                      }}
                    />
                  </div>
                </div>

                <div className="dr-checkboxRow">
                  <label className="dr-checkboxLabel">
                    <input
                      type="checkbox"
                      className="dr-checkboxInput"
                      checked={includeAllDevicesDatastreams}
                      onChange={(e) => {
                        setIncludeAllDevicesDatastreams(e.target.checked);
                        setAddBlockError('');
                      }}
                    />
                    <span>
                      {t.includeAllDevicesDatastreams || 'Include all devices with all datastreams'}
                    </span>
                  </label>
                </div>

                {!includeAllDevicesDatastreams && (
                  <React.Fragment>
                <div className="dr-rowBetween dr-devicesRow">
                  <span className="dr-subtitle">{t.devices}</span>
                  <button className="btn btn-apply add-device" type="button" onClick={handleAddBlock}>
                    {t.buttonAddDevice}
                  </button>
                </div>

                {addBlockError && <p className="dr-errorText">{addBlockError}</p>}

                {blocks.map((block) => {
                  const selectedDevice = block.device;

                  const filteredStreams =
                    selectedDevice?.template?.datastreams?.filter(isReportDownloadDatastream) || [];

                  const selectedPins = selectedStreamsByBlockId[block.id] || [];

                  const selectedDeviceIds = blocks
                    .filter((b) => b.device && b.device.did !== block.device?.did)
                    .map((b) => b.device.did);

                  return (
                    <div key={block.id} className="dr-block">
                      <span className="dr-blockRemove" onClick={() => handleRemoveBlock(block.id)}>
                        ✖
                      </span>

                      <div className="dr-blockGrid">
                        <div className="dr-half">
                          <label className="dr-label form-lbl">{t.labelDevice}</label>
                          <Select
                            isRtl={isRtl}
                            classNamePrefix="dr-select"
                            options={deviceSelectOptions.filter((option) => !selectedDeviceIds.includes(option.value))}
                            value={selectedDevice ? buildDeviceDropdownOption(selectedDevice, 'did') : null}
                            onChange={(opt) => handleSelectDevice(block.id, opt.value)}
                            placeholder={t.placeholderSelectDevice}
                          />
                        </div>

                        {selectedDevice && (
                          <div className="dr-half">
                            <label className="dr-label form-lbl">{t.labelDatastreams}</label>
                            <Select
                              isMulti
                              isRtl={isRtl}
                              classNamePrefix="dr-select"
                              options={[
                                { value: '__ALL__', label: t.optionAll },
                                ...filteredStreams.map((ds) => ({
                                  value: ds.pin,
                                  label: `${ds.name} (${ds.pin})`,
                                })),
                              ]}
                              value={
                                selectedPins.length === filteredStreams.length && filteredStreams.length > 0
                                  ? [{ value: '__ALL__', label: t.optionAll }]
                                  : selectedPins
                                      .map((pin) => {
                                        const ds = filteredStreams.find((d) => d.pin === pin);
                                        return ds ? { value: pin, label: `${ds.name} (${pin})` } : null;
                                      })
                                      .filter(Boolean)
                              }
                              onChange={(opts) => handleStreamChange(block.id, filteredStreams, opts)}
                              placeholder={t.placeholderSelectStreams}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                  </React.Fragment>
                )}

                <AiReportModePanel
                  access={reportAccess}
                  selectedMode={reportMode}
                  onModeChange={setReportMode}
                  labels={t.aiReportMode}
                />

                <AiReportVariablesStep
                  reportMode={resolvedReportMode}
                  reportType={dashboardType || null}
                  types={aiReportTypes}
                  variables={aiReportVariables}
                  onVariableChange={handleAiReportVariableChange}
                  labels={t.aiReportVariables}
                />

                <div className="dr-toggleRow form-item">
                  <label className="dr-label form-lbl">{t.labelSendEmail}</label>
                  <CustomSwitch checked={sendToEmail} onChange={(next) => setSendToEmail(next)} />
                </div>

                {sendToEmail && (
                  <div className="dr-field form-item">
                    <label className="dr-label form-lbl">{t.labelEmail}</label>
                    <input
                      type="email"
                      className="dr-input add-column"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t.placeholderEmail}
                    />
                  </div>
                )}

                <div className="dr-field form-item">
                  <label className="dr-label form-lbl">{t.labelDataAggregation}</label>
                  <div className="dr-pillRow details-time-range">
                    {aggregationOptions.map((range, idx) => (
                      <span
                        key={idx}
                        className={`dr-pill details-control-btn ${number === range.value ? 'is-active active' : ''}`}
                        onClick={() => setNumber(range.value)}
                      >
                        {range.label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="dr-footer modal-footer">
                <button className="btn btn-cancel" onClick={closeReportDialog}>
                  {t.buttonCancel}
                </button>

                <button className="btn btn-apply" onClick={handleDownload}>
                  {primaryActionLabel}
                </button>
              </div>
            </div>
          </div>
        </LocalizationProvider>
      </ThemeProvider>
    </React.Fragment>
  );
};
